import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e2e8f0",
    border: "#e2e8f0", borderFocus: "#6366f1",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    cardBg: "#ffffff",
    accent: "#6366f1", accentBg: "#eef2ff", accentText: "#4338ca",
    chipBg: "#f1f5f9", chipActive: "#6366f1", chipActiveBg: "#eef2ff",
    inputBg: "#ffffff",
  },
  dark: {
    bg: "#0a0f1e", bg2: "#111827", bg3: "#1f2937",
    border: "#1f2937", borderFocus: "#818cf8",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    cardBg: "#111827",
    accent: "#818cf8", accentBg: "#1e1b4b", accentText: "#a5b4fc",
    chipBg: "#1f2937", chipActive: "#818cf8", chipActiveBg: "#1e1b4b",
    inputBg: "#1f2937",
  },
};

const ITEMS = [
  { id: "calculatrice", label: "Calculatrice",       emoji: "🧮" },
  { id: "chargeur",     label: "Chargeur",           emoji: "🔌" },
  { id: "souris",       label: "Souris",             emoji: "🖱️" },
  { id: "casque",       label: "Casque / Écouteurs", emoji: "🎧" },
  { id: "cle_usb",      label: "Clé USB",            emoji: "💾" },
  { id: "telephone",    label: "Téléphone",          emoji: "📱" },
  { id: "livre",        label: "Livre / Cahier",     emoji: "📚" },
  { id: "lunettes",     label: "Lunettes",           emoji: "👓" },
  { id: "autre",        label: "Autre",              emoji: "📦" },
];

const ROOMS = [
  { id: "silent",    label: "Silent",    emoji: "🤫" },
  { id: "open",      label: "Open",      emoji: "💬" },
  { id: "smoking",   label: "Smoking",   emoji: "🚬" },
  { id: "focus",     label: "Focus",     emoji: "🎯" },
  { id: "exterieur", label: "Extérieur", emoji: "🌿" },
  { id: "booth",     label: "Booth",     emoji: "🪑" },
];

const COLORS = [
  { id: "noir",   label: "Noir",   hex: "#1a1a1a" },
  { id: "blanc",  label: "Blanc",  hex: "#f5f5f5", border: true },
  { id: "gris",   label: "Gris",   hex: "#9ca3af" },
  { id: "rouge",  label: "Rouge",  hex: "#ef4444" },
  { id: "bleu",   label: "Bleu",   hex: "#3b82f6" },
  { id: "vert",   label: "Vert",   hex: "#22c55e" },
  { id: "jaune",  label: "Jaune",  hex: "#eab308" },
  { id: "rose",   label: "Rose",   hex: "#ec4899" },
  { id: "orange", label: "Orange", hex: "#f97316" },
  { id: "violet", label: "Violet", hex: "#a855f7" },
  { id: "marron", label: "Marron", hex: "#92400e" },
  { id: "autre",  label: "Autre",  hex: null },
];

function Chip({ label, emoji, color, active, onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 12px", borderRadius: 10,
        border: `1.5px solid ${active ? t.chipActive : t.border}`,
        background: active ? t.chipActiveBg : t.chipBg,
        color: active ? t.accent : t.text2,
        fontSize: 12, fontWeight: active ? 700 : 500,
        cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
      }}
    >
      {emoji && <span style={{ fontSize: 14 }}>{emoji}</span>}
      {color && (
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          background: color.hex || "transparent",
          border: color.hex
            ? (color.border ? "1.5px solid #d1d5db" : "none")
            : `1.5px dashed ${t.text3}`,
          display: "inline-block", flexShrink: 0,
        }} />
      )}
      {label}
    </button>
  );
}

function StepIndicator({ step, total, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginBottom: 24 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: 4, borderRadius: 4,
          width: i === step - 1 ? 24 : 8,
          background: i < step ? t.accent : t.bg3,
          transition: "all 0.3s",
        }} />
      ))}
    </div>
  );
}

export default function LostFoundPage() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const t = dark ? theme.dark : theme.light;

  const [step, setStep]             = useState(1);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");

  // Auth user
  const [fullName, setFullName] = useState("");
  const [userId, setUserId]     = useState("");

  // Form
  const [item, setItem]               = useState("");
  const [itemOther, setItemOther]     = useState("");
  const [brand, setBrand]             = useState("");
  const [room, setRoom]               = useState("");
  const [color, setColor]             = useState("");
  const [when, setWhen]               = useState("");
  const [description, setDescription] = useState("");

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  // Pull fullname from contact/{uid}
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    setUserId(user.uid);
    getDoc(doc(db, "contact", user.uid))
      .then(snap => {
        if (snap.exists()) {
          setFullName(snap.data().fullname || snap.data().fullName || user.displayName || "");
        } else {
          setFullName(user.displayName || "");
        }
      })
      .catch(() => setFullName(user.displayName || ""));
  }, []);

  const canNext1 = item !== "" && (item !== "autre" || itemOther.trim() !== "");
  const canNext2 = room !== "" && color !== "" && when !== "";

  const labelOf = (arr, id) => arr.find(x => x.id === id)?.label || id;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await addDoc(collection(db, "lost_found"), {
        item:        item === "autre" ? itemOther.trim() : item,
        itemLabel:   item === "autre" ? itemOther.trim() : labelOf(ITEMS, item),
        brand:       brand.trim(),
        room,
        color,
        colorLabel:  labelOf(COLORS, color),
        when,
        description: description.trim(),
        contact:     fullName,
        userId,
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue. Réessayez.");
    }
    setSubmitting(false);
  };

  return (
    <div style={{
      background: t.bg, minHeight: "100vh",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ws-input {
          width: 100%; box-sizing: border-box;
          padding: 11px 14px; border-radius: 12px;
          font-size: 13px; font-family: inherit;
          outline: none; transition: border-color 0.15s;
        }
        .ws-input:focus { border-color: #6366f1; }
      `}</style>

      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 48 }}>

        {/* Topbar */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", padding: "16px 18px 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy"
              style={{ height: 36, objectFit: "contain" }}
              onError={e => e.target.style.display = "none"}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text1 }}>Lost & Found</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Signaler un objet perdu</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{
              width: 36, height: 20, borderRadius: 20, cursor: "pointer",
              position: "relative", background: t.bg2,
              border: `1px solid ${t.border}`, flexShrink: 0,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: "50%",
                background: t.accent, position: "absolute",
                top: 2, left: 2, transition: "transform 0.2s",
                transform: dark ? "translateX(16px)" : "translateX(0)",
              }} />
            </button>
            <button onClick={() => navigate("/")} style={{
              fontSize: 12, borderRadius: 8, padding: "6px 10px",
              cursor: "pointer", background: t.bg2,
              border: `1px solid ${t.border}`, color: t.text2,
            }}>
              ← Retour
            </button>
          </div>
        </div>

        {/* User pill */}
        {fullName && (
          <div style={{
            margin: "14px 18px 0",
            display: "inline-flex", alignItems: "center", gap: 8,
            background: t.accentBg, border: `1px solid ${t.border}`,
            borderRadius: 20, padding: "6px 12px",
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: "50%",
              background: t.accent, display: "flex",
              alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 800, color: "#fff",
            }}>
              {fullName.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: t.accentText }}>
              {fullName}
            </span>
          </div>
        )}

        {/* Card */}
        <div style={{
          margin: "14px 18px 0",
          background: t.cardBg,
          border: `1.5px solid ${t.border}`,
          borderRadius: 20,
          padding: "24px 20px",
          animation: "fadeSlide 0.3s ease",
        }}>

          {submitted ? (
            /* ── Success ── */
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 8 }}>
                Signalement envoyé !
              </div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 20 }}>
                Notre équipe va vérifier les objets trouvés et contactera{" "}
                <strong style={{ color: t.text1 }}>{fullName}</strong> si l'objet est retrouvé.
              </div>

              <div style={{
                background: t.accentBg, border: `1px solid ${t.border}`,
                borderRadius: 12, padding: "14px", marginBottom: 24,
                textAlign: "left", fontSize: 12, color: t.accentText, lineHeight: 1.9,
              }}>
                🔹 <strong>Objet :</strong> {item === "autre" ? itemOther : labelOf(ITEMS, item)}<br />
                {brand && <><strong>🔹 Marque :</strong> {brand}<br /></>}
                🔹 <strong>Salle :</strong> {labelOf(ROOMS, room)}<br />
                🔹 <strong>Couleur :</strong> {labelOf(COLORS, color)}<br />
                🔹 <strong>Quand :</strong> {new Date(when).toLocaleString("fr-TN")}<br />
                🔹 <strong>Déclarant :</strong> {fullName}
              </div>

              <button onClick={() => navigate("/")} style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                background: t.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}>
                Retour à l'accueil
              </button>
            </div>

          ) : (
            <>
              <StepIndicator step={step} total={2} t={t} />

              {/* ── Step 1 : Quoi ── */}
              {step === 1 && (
                <div style={{ animation: "fadeSlide 0.25s ease" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, letterSpacing: "0.08em", marginBottom: 4 }}>
                    ÉTAPE 1 / 2
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 4 }}>
                    Qu'avez-vous perdu ?
                  </div>
                  <div style={{ fontSize: 12, color: t.text2, marginBottom: 20 }}>
                    Sélectionnez le type d'objet perdu.
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    {ITEMS.map(it => (
                      <Chip key={it.id} label={it.label} emoji={it.emoji}
                        active={item === it.id} onClick={() => setItem(it.id)} t={t} />
                    ))}
                  </div>

                  {item === "autre" && (
                    <div style={{ marginBottom: 14 }}>
                      <input
                        className="ws-input"
                        placeholder="Précisez l'objet…"
                        value={itemOther}
                        onChange={e => setItemOther(e.target.value)}
                        style={{ background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.text1 }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 6 }}>
                      Marque <span style={{ color: t.text3, fontWeight: 400 }}>(optionnel)</span>
                    </label>
                    <input
                      className="ws-input"
                      placeholder="Ex : Apple, Samsung, Casio…"
                      value={brand}
                      onChange={e => setBrand(e.target.value)}
                      style={{ background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.text1 }}
                    />
                  </div>
                </div>
              )}

              {/* ── Step 2 : Où, quand & détails ── */}
              {step === 2 && (
                <div style={{ animation: "fadeSlide 0.25s ease" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, letterSpacing: "0.08em", marginBottom: 4 }}>
                    ÉTAPE 2 / 2
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 4 }}>
                    Où & quand ?
                  </div>
                  <div style={{ fontSize: 12, color: t.text2, marginBottom: 20 }}>
                    Plus de détails = plus de chances de retrouver votre objet.
                  </div>

                  {/* Room */}
                  <div style={{ marginBottom: 18 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 8 }}>
                      Salle *
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {ROOMS.map(r => (
                        <Chip key={r.id} label={r.label} emoji={r.emoji}
                          active={room === r.id} onClick={() => setRoom(r.id)} t={t} />
                      ))}
                    </div>
                  </div>

                  {/* Color */}
                  <div style={{ marginBottom: 18 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 8 }}>
                      Couleur principale *
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {COLORS.map(c => (
                        <Chip key={c.id} label={c.label} color={c}
                          active={color === c.id} onClick={() => setColor(c.id)} t={t} />
                      ))}
                    </div>
                  </div>

                  {/* When */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 6 }}>
                      Quand l'avez-vous perdu ? *
                    </label>
                    <input
                      type="datetime-local"
                      className="ws-input"
                      value={when}
                      onChange={e => setWhen(e.target.value)}
                      style={{
                        background: t.inputBg, border: `1.5px solid ${t.border}`,
                        color: t.text1, colorScheme: dark ? "dark" : "light",
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 6 }}>
                      Détails supplémentaires <span style={{ color: t.text3, fontWeight: 400 }}>(optionnel)</span>
                    </label>
                    <textarea
                      className="ws-input"
                      placeholder="Autocollants, gravure, détails distinctifs…"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      rows={3}
                      style={{
                        background: t.inputBg, border: `1.5px solid ${t.border}`,
                        color: t.text1, resize: "vertical",
                      }}
                    />
                  </div>

                  {error && (
                    <div style={{
                      marginTop: 12, padding: "10px 14px", borderRadius: 10,
                      background: "#fef2f2", border: "1px solid #fecaca",
                      fontSize: 12, color: "#dc2626",
                    }}>
                      ⚠️ {error}
                    </div>
                  )}
                </div>
              )}

              {/* Nav */}
              <div style={{
                display: "flex", gap: 10, marginTop: 24,
                justifyContent: step === 1 ? "flex-end" : "space-between",
              }}>
                {step > 1 && (
                  <button onClick={() => setStep(s => s - 1)} style={{
                    padding: "12px 20px", borderRadius: 12, cursor: "pointer",
                    background: t.bg2, border: `1.5px solid ${t.border}`,
                    color: t.text2, fontSize: 13, fontWeight: 600,
                  }}>
                    ← Retour
                  </button>
                )}
                {step === 1 && (
                  <button
                    onClick={() => setStep(2)}
                    disabled={!canNext1}
                    style={{
                      flex: 1, padding: "12px", borderRadius: 12,
                      cursor: canNext1 ? "pointer" : "default",
                      background: canNext1 ? t.accent : t.bg3,
                      border: "none",
                      color: canNext1 ? "#fff" : t.text3,
                      fontSize: 13, fontWeight: 700, transition: "background 0.2s",
                    }}
                  >
                    Suivant →
                  </button>
                )}
                {step === 2 && (
                  <button
                    onClick={handleSubmit}
                    disabled={!canNext2 || submitting}
                    style={{
                      flex: 1, padding: "12px", borderRadius: 12,
                      cursor: canNext2 && !submitting ? "pointer" : "default",
                      background: canNext2 ? t.accent : t.bg3,
                      border: "none",
                      color: canNext2 ? "#fff" : t.text3,
                      fontSize: 13, fontWeight: 700,
                    }}
                  >
                    {submitting ? "Envoi…" : "📨 Envoyer le signalement"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer note */}
        {!submitted && (
          <div style={{
            margin: "14px 18px 0", background: t.bg2,
            border: `1px solid ${t.border}`, borderRadius: 12,
            padding: "10px 14px", display: "flex", gap: 8, alignItems: "center",
          }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <span style={{ fontSize: 11, color: t.text2, lineHeight: 1.5 }}>
              Vous pouvez aussi passer directement à{" "}
              <strong style={{ color: t.text1 }}>l'accueil WeStudy</strong> pour signaler votre perte en personne.
            </span>
          </div>
        )}

      </div>
    </div>
  );
}